import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { useTranslation } from '@/core/i18n/i18n';

interface OutOfTimeRequestDialogProps {
  open: boolean;
  onClose: () => void;
  isSubmitting: boolean;
  onSubmit: (reason: string) => Promise<void> | void;
}

const OutOfTimeRequestDialog: React.FC<OutOfTimeRequestDialogProps> = ({
  open,
  onClose,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed || isSubmitting) return;
    Promise.resolve(onSubmit(trimmed)).catch((error) => {
      console.error('Error submitting out-of-time request:', error);
    });
  };

  const reasonInvalid = !reason.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1, boxShadow: 4 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>
        {t('annualPlanning.outOfTimeRequestDialogTitle')}
      </DialogTitle>
      <DialogContent sx={{ px: 4, py: 2 }}>
        <Typography variant="body2" sx={{ color: '#555', mb: 3, textAlign: 'center' }}>
          {t('annualPlanning.outOfTimeRequestDialogDescription')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
            {t('annualPlanning.outOfTimeRequestReasonLabel')}{' '}
            <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
            rows={4}
            placeholder={t('annualPlanning.outOfTimeRequestReasonPlaceholder')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 6,
              border: '1px solid #c4cdd5',
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              resize: 'vertical',
              outline: 'none',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
            }}
          />
          {reasonInvalid && (
            <Typography variant="caption" sx={{ color: '#d32f2f' }}>
              {t('annualPlanning.outOfTimeRequestReasonRequired')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            borderColor: '#00BCE4',
            color: '#00BCE4',
            px: 3,
            borderRadius: 20,
            textTransform: 'none',
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting || reasonInvalid}
          sx={{
            bgcolor: '#00BCE4',
            color: '#fff',
            px: 4,
            borderRadius: 20,
            textTransform: 'none',
            '&:hover': { bgcolor: '#0099b3' },
          }}
        >
          {t('annualPlanning.requestOutOfTimePlan')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(OutOfTimeRequestDialog);
